import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Brand, ThemeClasses } from "../types";
import { ModelGridCard } from "./ModelGridCard";
import { getTimeAgo, type TimeTranslations } from "../../../../../../utils/getDate";

interface ModelListProps {
  brands: Brand[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedView: "grid" | "list";
  onEdit: (brand: Brand) => void;
  onDetails: (brand: Brand) => void;
  onCommands: (brand: Brand) => void;
  t: (key: string, options?: any) => string;
}

export function ModelList({
  brands,
  themeClasses,
  isDarkMode,
  selectedView,
  onEdit,
  onDetails,
  onCommands,
  t,
}: ModelListProps) {
  const { t: tTranslate } = useTranslation("lite");

  const timeTranslations: TimeTranslations = useMemo(() => ({
    noData: tTranslate("time.noData"),
    rightNow: tTranslate("time.rightNow"),
    min: tTranslate("time.min"),
    hour: tTranslate("time.hour"),
    hours: tTranslate("time.hours"),
    day: tTranslate("time.day"),
    days: tTranslate("time.days"),
    month: tTranslate("time.month"),
    months: tTranslate("time.months"),
    and: tTranslate("time.and"),
  }), [tTranslate]);

  if (brands.length === 0) {
    return (
      <div className={`text-center py-12 ${themeClasses.textSecondary}`}>
        <i className="ri-cpu-line text-5xl mb-4 block opacity-50"></i>
        <p className="text-lg font-medium">{t("models.noModels")}</p>
        <p className="text-sm">{t("models.noModelsDescription")}</p>
      </div>
    );
  }

  if (selectedView === "grid") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {brands.map((brand) => (
          <ModelGridCard
            key={brand.id}
            brand={brand}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onEdit={onEdit}
            onDetails={onDetails}
            onCommands={onCommands}
            t={t}
          />
        ))}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-2">
      {brands.map((brand) => (
        <div
          key={brand.id}
          className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-4 flex items-center justify-between`}
        >
          <div className="flex items-center space-x-4">
            <div
              className={`w-10 h-10 ${isDarkMode ? "bg-blue-900/30" : "bg-blue-100"} text-blue-500 rounded-xl flex items-center justify-center`}
            >
              <i className="ri-cpu-line text-lg"></i>
            </div>
            <div>
              <h3 className={`font-semibold ${themeClasses.text}`}>
                {brand.brand} - {brand.model}
              </h3>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {brand.operational_alias || t("models.noAlias")} | {brand.network?.protocol || "N/A"}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center gap-2">
              {brand.origin && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  brand.origin === "v5"
                    ? "bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white"
                    : brand.origin === "v4"
                    ? "bg-green-500 text-white dark:bg-green-600 dark:text-white"
                    : "bg-teal-400 text-teal-900 dark:bg-teal-600 dark:text-white"
                }`}>
                  {brand.origin.toUpperCase()}
                </span>
              )}
              {brand.collections?.includes("commands") && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-600 text-white dark:bg-purple-500 dark:text-white hidden sm:inline">
                  {t("models.hasCommands")}
                </span>
              )}
            </div>
            <span className={`text-xs ${themeClasses.textSecondary} hidden md:block`}>
              {t("models.lastUpdate")}:{" "}
              {brand.updatedAt?._seconds
                ? getTimeAgo(brand.updatedAt._seconds, timeTranslations)
                : "N/A"}
            </span>
            <div className="flex items-center space-x-2">
            <button
              onClick={() => onCommands(brand)}
              className={`p-2 ${isDarkMode ? "hover:bg-purple-900/30 text-purple-400" : "hover:bg-purple-100 text-purple-600"} rounded-lg transition-colors`}
              title={t("models.actions.commands")}
            >
              <i className="ri-terminal-box-line"></i>
            </button>
            <button
              onClick={() => onEdit(brand)}
              className={`p-2 ${themeClasses.buttonHover} ${themeClasses.buttonText} rounded-lg transition-colors`}
              title={t("models.actions.edit")}
            >
              <i className="ri-edit-line"></i>
            </button>
            <button
              onClick={() => onDetails(brand)}
              className="p-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg transition-colors"
              title={t("models.actions.details")}
            >
              <i className="ri-eye-line"></i>
            </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
